import { useState, useEffect } from 'react';
import axios from 'axios';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { AdminLayout } from '../../layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { StatusBadge, LiveIndicator } from '../../components/LiveIndicator';
import { 
  TrendingUp,
  DollarSign,
  AlertTriangle,
  Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = '/api';

export function ExposureView() {
  const [races, setRaces] = useState([]);
  const [selectedRaceId, setSelectedRaceId] = useState('');
  const [raceExposure, setRaceExposure] = useState(null);
  const [loading, setLoading] = useState(true);
  const { subscribeToType, isConnected } = useWebSocket();

  const fetchRaces = async () => {
    try {
      const response = await axios.get(`${API}/races`);
      const activeRaces = response.data.filter(r => ['open', 'suspended'].includes(r.status));
      setRaces(activeRaces);
      if (activeRaces.length > 0 && !selectedRaceId) {
        setSelectedRaceId(activeRaces[0].id);
      }
    } catch (error) {
      toast.error('Failed to load races');
    } finally {
      setLoading(false);
    }
  };

  const fetchExposure = async (raceId) => {
    if (!raceId) return;
    try {
      const response = await axios.get(`${API}/exposure/race/${raceId}`);
      setRaceExposure(response.data);
    } catch (error) {
      console.error('Failed to fetch exposure:', error);
    }
  };

  useEffect(() => {
    fetchRaces();
  }, []);

  useEffect(() => {
    if (selectedRaceId) {
      fetchExposure(selectedRaceId);
    }
  }, [selectedRaceId]);

  // Real-time updates via WebSocket + polling fallback
  useEffect(() => {
    const unsubscribe = subscribeToType('*', (data) => {
      if (data.type === 'bet_placed' || data.type === 'bet_cancelled' || data.type === 'race_settled') {
        if (selectedRaceId) {
          fetchExposure(selectedRaceId);
        }
        fetchRaces();
      } else if (data.type === 'race_status_changed') {
        fetchRaces();
      }
    });

    // Polling fallback every 5 seconds for reliable updates
    const interval = setInterval(() => {
      if (selectedRaceId) {
        fetchExposure(selectedRaceId);
      }
    }, 5000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [subscribeToType, selectedRaceId]);

  const selectedRace = races.find(r => r.id === selectedRaceId);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'd MMM');
    } catch {
      return dateStr;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="exposure-view">
        {/* Header with Race Selector */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-zinc-100">Race Exposure</h1>
            <p className="text-sm text-zinc-500">Monitor liability in real-time</p>
          </div>
          {isConnected && <LiveIndicator />}
        </div>

        {/* Race Selector */}
        {loading ? (
          <div className="h-14 bg-zinc-900 animate-pulse rounded border border-zinc-800" />
        ) : races.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-8 text-center text-zinc-500">
              <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No active races with exposure</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Select value={selectedRaceId} onValueChange={setSelectedRaceId}>
              <SelectTrigger 
                className="w-full h-14 bg-zinc-900 border-zinc-800 text-zinc-100"
                data-testid="exposure-race-selector"
              >
                <SelectValue>
                  {selectedRace && (
                    <div className="flex items-center gap-3">
                      <Trophy className="w-5 h-5 text-blue-500" />
                      <span className="font-heading text-base font-bold">{selectedRace.venue}</span>
                      <span className="font-heading text-base font-bold text-blue-400">{selectedRace.time}</span>
                      <span className="text-xs text-zinc-500">{formatDate(selectedRace.date)}</span>
                      <StatusBadge status={selectedRace.status} />
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {races.map(race => (
                  <SelectItem key={race.id} value={race.id} className="py-3">
                    <div className="flex items-center gap-3">
                      <Trophy className="w-4 h-4 text-blue-500" />
                      <span className="font-bold">{race.venue}</span>
                      <span className="font-bold text-blue-400">{race.time}</span>
                      <span className="text-xs text-zinc-500">{formatDate(race.date)}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Exposure Summary */}
            {raceExposure && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <Card className="bg-zinc-900 border-zinc-800">
                    <CardContent className="p-3 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500">Total Stakes</p>
                      <p className="font-mono text-xl font-bold text-emerald-400" data-testid="race-total-stakes">
                        €{raceExposure.total_stakes?.toFixed(2)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-zinc-900 border-zinc-800 col-span-2">
                    <CardContent className="p-3">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500 text-center mb-2">
                        Max liability (if single winner)
                      </p>
                      <div className="grid grid-cols-2 gap-3 text-center">
                        <div>
                          <p className="text-[9px] uppercase tracking-wider text-zinc-500">Max win leg</p>
                          <p className="text-[8px] text-zinc-600 normal-case tracking-normal mb-0.5">worst single runner</p>
                          <p className="font-mono text-lg font-bold text-red-400" data-testid="race-max-win-liability">
                            €{(raceExposure.max_win_liability ?? 0).toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[9px] uppercase tracking-wider text-zinc-500">Max place leg</p>
                          <p className="text-[8px] text-zinc-600 normal-case tracking-normal mb-0.5">worst single runner</p>
                          <p className="font-mono text-lg font-bold text-amber-400" data-testid="race-max-place-liability">
                            €{(raceExposure.max_place_liability ?? 0).toFixed(2)}
                          </p>
                        </div>
                      </div>
                      <p className="text-[10px] text-zinc-500 text-center mt-2 font-mono border-t border-zinc-800 pt-2">
                        All open bets — win legs: €{(raceExposure.total_win_leg ?? 0).toFixed(2)}
                        <span className="text-zinc-600 mx-1">·</span>
                        place legs: €{(raceExposure.total_place_leg ?? 0).toFixed(2)}
                      </p>
                      <p className="text-[10px] text-zinc-500 text-center mt-1 font-mono">
                        Combined peak (same runner wins): €{raceExposure.max_liability?.toFixed(2)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-zinc-900 border-zinc-800">
                    <CardContent className="p-3 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500">Runners</p>
                      <p className="font-mono text-xl font-bold text-zinc-100">
                        {raceExposure.runners?.length || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-zinc-900 border-zinc-800">
                    <CardContent className="p-3 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-500">Total Bets</p>
                      <p className="font-mono text-xl font-bold text-zinc-100">
                        {raceExposure.runners?.reduce((sum, r) => sum + r.bet_count, 0) || 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Runners Exposure Table */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader className="py-3">
                    <CardTitle className="text-base font-heading text-zinc-100 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-blue-500" />
                      <span>{selectedRace?.venue}</span>
                      <span className="text-blue-400">{selectedRace?.time}</span>
                      <span className="text-xs text-zinc-500 font-normal ml-2">Exposure by Runner</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-zinc-800 hover:bg-transparent">
                            <TableHead className="text-zinc-400 w-12">#</TableHead>
                            <TableHead className="text-zinc-400">Runner</TableHead>
                            <TableHead className="text-zinc-400 text-right">Odds</TableHead>
                            <TableHead className="text-zinc-400 text-right">Bets</TableHead>
                            <TableHead className="text-zinc-400 text-right">Stakes</TableHead>
                            <TableHead className="text-zinc-400 text-right">Win liab.</TableHead>
                            <TableHead className="text-zinc-400 text-right">Place liab.</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {raceExposure.runners?.map(runner => (
                            <TableRow key={runner.runner_id} className="border-zinc-800">
                              <TableCell className="font-mono text-zinc-400">{runner.runner_number}</TableCell>
                              <TableCell className="font-medium text-zinc-100">{runner.runner_name}</TableCell>
                              <TableCell className="text-right font-mono text-zinc-100">{runner.odds_display}</TableCell>
                              <TableCell className="text-right font-mono text-zinc-400">{runner.bet_count}</TableCell>
                              <TableCell className="text-right font-mono text-emerald-400">€{runner.total_stakes?.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-mono text-red-400">€{(runner.win_liability ?? 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right font-mono text-amber-400">€{(runner.place_liability ?? 0).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
